import { c as _c } from "react/compiler-runtime";
import { useSetAtom } from "jotai";
import { Wand, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@repo/blacksmith-ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useActiveGithubOrgForUser, useHasRunFirstJobLive, useIsPersonalOrgNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDashboardRouter } from "@/hooks/useDashboardRouter";
import { useUpdateUserHomeSettings, useUserHomeSettings } from "@/hooks/useUserHomeSettings";
import { isHomePath } from "@/components/sidebar/useActiveApp";
import { PixelTrailCanvas } from "./PixelTrailCanvas";
import { migrationWizardOpenAtom } from "./migrationWizardState";
const LEARN_MORE_URL = "https://docs.blacksmith.sh/introduction/quickstart";

/**
 * Live height of the mounted MigrateCiHeader, published on the document root.
 * The fixed sidebar container in ui/sidebar.tsx reads it as `top` so every
 * sidebar starts below the header and tracks its collapse (0px when absent).
 */
const MIGRATE_CI_HEADER_HEIGHT_CSS_VAR = "--migrate-ci-header-height";

/**
 * Layout-level MigrateCiHeader: spans the full app width above the rail and
 * sidebars on home while the active org has yet to run a job, until the user
 * dismisses it (saved per user and org on the server).
 * Suspends on the org list and, on home, on the saved dismissal, so mount it
 * under its own Suspense boundary.
 */
export function MigrateCiLayoutHeader() {
  const $ = _c(11);
  const router = useDashboardRouter();
  let t0;
  if ($[0] !== router.pathname) {
    t0 = isHomePath(router.pathname);
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onHome = t0;
  const {
    activeGithubOrg,
    activeGithubOrgHasRunFirstJob
  } = useActiveGithubOrgForUser();
  const hasRunFirstJobLive = useHasRunFirstJobLive(!activeGithubOrgHasRunFirstJob);
  const isPersonalOrg = useIsPersonalOrgNonSuspense();
  const wantsHeader = !(activeGithubOrgHasRunFirstJob || hasRunFirstJobLive);
  const [mountedOrg] = useState(activeGithubOrg);
  const t1 = onHome && activeGithubOrg === mountedOrg;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      suspense: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const homeSettings = useUserHomeSettings(wantsHeader ? activeGithubOrg : null, t2);
  const updateHomeSettings = useUpdateUserHomeSettings(activeGithubOrg);
  if (!onHome) {
    return null;
  }
  if (!activeGithubOrg) {
    return null;
  }
  if (homeSettings.isLoading) {
    return null;
  }
  const dismissed = homeSettings.data?.migrate_banner_dismissed === true;
  const t3 = activeGithubOrg ?? "";
  const t4 = wantsHeader && !dismissed;
  let t5;
  if ($[4] !== updateHomeSettings) {
    t5 = () => updateHomeSettings.mutate({
      migrate_banner_dismissed: true
    });
    $[4] = updateHomeSettings;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isPersonalOrg || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = <MigrateCiHeader key={t3} visible={t4} isPersonalOrg={isPersonalOrg} onDismiss={t5} />;
    $[6] = isPersonalOrg;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/**
 * Yellow band above a page's content that nudges orgs which haven't run a
 * job on Blacksmith yet. It mounts already open, grows down when `visible`
 * later flips on, and collapses when it flips off, so the page underneath
 * follows the motion.
 */
export function MigrateCiHeader(t0) {
  const $ = _c(12);
  const {
    visible,
    isPersonalOrg,
    onDismiss
  } = t0;
  const setMigrationWizardOpen = useSetAtom(migrationWizardOpenAtom);
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== reduceMotion) {
    t1 = reduceMotion ? {
      duration: 0
    } : {
      duration: 0.45,
      ease: [0.33, 1, 0.68, 1] as const
    };
    $[0] = reduceMotion;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const transition = t1;
  const frameRef = useRef(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const frame = frameRef.current;
      if (!frame) {
        return;
      }
      const root = document.documentElement;
      const publish = () => root.style.setProperty(MIGRATE_CI_HEADER_HEIGHT_CSS_VAR, `${frame.getBoundingClientRect().height}px`);
      publish();
      const observer = new ResizeObserver(publish);
      observer.observe(frame);
      return () => {
        observer.disconnect();
        root.style.removeProperty(MIGRATE_CI_HEADER_HEIGHT_CSS_VAR);
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  let t4;
  if ($[4] !== isPersonalOrg || $[5] !== onDismiss || $[6] !== setMigrationWizardOpen || $[7] !== transition || $[8] !== visible) {
    t4 = visible && <motion.section key="migrate-ci-header" aria-label="Migrate your workflows into Blacksmith" initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={transition} className="relative shrink-0 overflow-hidden bg-[var(--blacksmith-yellow)] text-[var(--blacksmith-black)] [box-shadow:inset_0_-8px_12px_-8px_rgba(0,0,0,0.35)]"><PixelTrailCanvas surface="light" className="rounded-none" />{onDismiss && <button type="button" onClick={onDismiss} aria-label="Dismiss migration banner" className="absolute right-3 top-3 z-20 cursor-pointer rounded-md p-1 text-[var(--blacksmith-black)] hover:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]"><X className="size-4" strokeWidth={2.25} aria-hidden={true} /></button>}<div className="relative z-10 flex flex-col items-center gap-5 px-6 py-8 text-center"><h2 className="type-page-title-l">Migrate your workflows into Blacksmith</h2><div className="flex flex-wrap items-center justify-center gap-2"><Button variant="secondary" asChild={true} className="border-black/20 text-[var(--blacksmith-black)] hover:border-black/20 hover:bg-black/10 dark:text-[var(--blacksmith-black)] dark:hover:bg-black/10 dark:hover:text-[var(--blacksmith-black)]"><a href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">Learn more</a></Button>{isPersonalOrg === undefined ? <Button variant="default" aria-disabled={true} className="gap-2 dark:bg-[#313435] dark:text-white dark:hover:bg-[#161718] dark:hover:text-white"><Wand className="size-4" aria-hidden={true} />Migration Wizard</Button> : isPersonalOrg ? <Tooltip delayDuration={0}><TooltipTrigger asChild={true}><span tabIndex={0}><Button variant="default" disabled={true} className="gap-2"><Wand className="size-4" aria-hidden={true} />Migration Wizard</Button></span></TooltipTrigger><TooltipContent>Blacksmith doesn't support personal orgs</TooltipContent></Tooltip> : <Button variant="default" onClick={() => setMigrationWizardOpen(true)} className="gap-2 dark:bg-[#313435] dark:text-white dark:hover:bg-[#161718] dark:hover:text-white"><Wand className="size-4" aria-hidden={true} />Migration Wizard</Button>}</div></div></motion.section>;
    $[4] = isPersonalOrg;
    $[5] = onDismiss;
    $[6] = setMigrationWizardOpen;
    $[7] = transition;
    $[8] = visible;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <div ref={frameRef} className="shrink-0"><AnimatePresence initial={false}>{t4}</AnimatePresence></div>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}